"use strict";

exports.__esModule = true;
exports.default = exports.Switch = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel3 = _interopRequireDefault(require("../../hooks/useLabel"));
var _excluded = ["checked", "type", "id", "name", "label", "helper", "required", "disabled", "readOnly", "size", "color", "outline", "align", "onChange", "onBlur", "onError", "containerLabel", "className", "children"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * The Switch component allows the user to make a binary choice, i.e. a choice between one of two possible mutually exclusive options.
 */
var Switch = exports.Switch = function Switch(props) {
  var controlledChecked = props.checked,
    type = props.type,
    controlledId = props.id,
    name = props.name,
    initialLabel = props.label,
    helper = props.helper,
    required = props.required,
    disabled = props.disabled,
    readOnly = props.readOnly,
    size = props.size,
    color = props.color,
    _props$outline = props.outline,
    outline = _props$outline === void 0 ? true : _props$outline,
    align = props.align,
    onChange = props.onChange,
    onBlur = props.onBlur,
    onError = props.onError,
    initialContainerLabel = props.containerLabel,
    className = props.className,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, _excluded);
  var switchRef = _react.default.useRef(null);
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var _React$useState = _react.default.useState(controlledChecked || false),
    checked = _React$useState[0],
    setChecked = _React$useState[1];
  var _React$useState2 = _react.default.useState(false),
    error = _React$useState2[0],
    setError = _React$useState2[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(checked, {
      required: required
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  var _useLabel = (0, _useLabel3.default)(initialLabel, _extends({}, props, {
      id: id,
      required: required && !initialContainerLabel
    }), true),
    label = _useLabel.label;
  var _useLabel2 = (0, _useLabel3.default)(initialContainerLabel, _extends({}, props, {
      id: id,
      required: required
    }), true),
    containerLabel = _useLabel2.label;
  _react.default.useEffect(function () {
    setChecked(controlledChecked || false);
  }, [controlledChecked]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: switchRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);
  var handleChange = function handleChange(event) {
    methods.onChangeCheckbox(event);
    setChecked(event.target.checked);
    if (typeof onChange === 'function') {
      onChange(event.target.checked, switchRef.current.name, event);
    }
  };
  var handleBlur = function handleBlur(event) {
    methods.onChangeCheckbox(event);
    if (typeof onBlur === 'function') {
      onBlur(event.target.id);
    }
  };
  var classes = (0, _clsx.default)('uk-form-item', 'uk-switch', color !== 'default' && color, size !== 'default' && size, align !== 'left' && 'reverse', disabled && 'disabled', helper.length > 0 && 'has-helper', errors.length > 0 && 'has-error', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: classes
  }, containerLabel && /*#__PURE__*/_react.default.createElement("div", {
    className: "switch-container-label"
  }, containerLabel), /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: id,
    className: (0, _clsx.default)('flex-container', label && align !== 'left' && align)
  }, /*#__PURE__*/_react.default.createElement("input", _extends({
    ref: switchRef,
    type: type,
    id: id,
    name: name || id,
    checked: checked,
    onChange: handleChange,
    onBlur: handleBlur,
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    role: "switch",
    "aria-checked": checked,
    "aria-readonly": readOnly
  }, attributes, {
    className: "uk-checkable"
  })), /*#__PURE__*/_react.default.createElement("span", {
    className: (0, _clsx.default)('switch-paddle uk-fake-input', color !== 'default' && color, outline ? "uk-outline--" + color : checked ? "uk-bgcolor--" + color + " uk-color--white" : '')
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-svg"
  }, /*#__PURE__*/_react.default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/_react.default.createElement("circle", {
    cx: "50%",
    cy: "50%",
    r: ".4em"
  })))), label && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkable__label"
  }, label)), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper"
  }, error ? errors : helper));
};
Switch.propTypes = {
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the Switch (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Change the behavior of the Switch(es)
   */
  type: _propTypes.default.oneOf(['checkbox', 'radio']),
  /**
   * Label of the Switch
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Position of the Switch relative to its label
   */
  align: _propTypes.default.oneOf(['left', 'right', 'vertical']),
  /**
   * Size of the Switch
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of an active Switch
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'alert', 'success', 'warning']),
  /**
   * Set the Switch as active
   */
  checked: _propTypes.default.bool,
  /**
   * Add a high-level label
   */
  containerLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Set the Switch as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the Switch as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the Switch as required
   */
  required: _propTypes.default.bool,
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func
};
Switch.defaultProps = {
  helper: '',
  type: 'checkbox',
  align: 'right',
  size: 'default',
  color: 'primary',
  outline: false,
  readOnly: false,
  disabled: false,
  required: false
};
var _default = exports.default = Switch;